import Component from '@glimmer/component';
import { service } from '@ember/service';
import { get, setProperties, action } from '@ember/object';
import { format, endOfDay, formatISO, startOfDay, subMinutes, getUnixTime } from 'date-fns';
import { fromUnixTime } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { task } from 'ember-concurrency';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import normalizeResponse from 'garaje/utils/normalize-response';
import urlBuilder from 'garaje/utils/url-builder';
import { tracked } from '@glimmer/tracking';

/**
 * @param {Desk}                    desk
 * @param {string}                  text
 */
export default class AvailableDeskPopup extends Component {
  @service abilities;
  @service state;
  @service store;
  @service flashMessages;
  @service metrics;
  @service ajax;
  @service liveMap;
  @service featureFlags;

  @tracked isAmenitiesExpanded = false;

  get isPartialDayEnabled() {
    return this.abilities.can('use partial day booking desk');
  }

  get formattedDate() {
    const displayDate = subMinutes(this.args.date, this.state.minutesBetweenTimezones(this.args.date));
    return format(displayDate, 'MMMM dd, yyyy');
  }

  get selectedStartDateForRequests() {
    return this.args.selectedTime != null ? fromUnixTime(this.args.selectedTime) : this.args.date;
  }

  get selectedEndDateForRequests() {
    return this.args.selectedEndTime != null ? fromUnixTime(this.args.selectedEndTime) : this.locationEndTime;
  }

  get isPartialDay() {
    return this.isPartialDayEnabled && this.args.selectedTime != null && this.args.selectedEndTime != null;
  }

  get formattedStartTime() {
    if (!this.isPartialDay) return '';
    const start = fromUnixTime(this.args.selectedTime);
    const displayStart = subMinutes(start, this.state.minutesBetweenTimezones(start));
    return format(displayStart, 'h:mm a');
  }

  get formattedEndTime() {
    if (!this.isPartialDay) return '';
    const end = fromUnixTime(this.args.selectedEndTime);
    const displayEnd = subMinutes(end, this.state.minutesBetweenTimezones(end));
    return format(displayEnd, 'h:mm a');
  }

  get locationEndTime() {
    return this.state.getOfficeLocationTime(endOfDay(toZonedTime(this.args.date, this.state.currentLocation.timezone)));
  }

  get amenitiesToDisplay() {
    const { desk } = this.args;

    if (this.isAmenitiesExpanded) {
      return desk.amenities;
    }

    return desk.amenities.content.slice(0, 2);
  }

  @action
  toggleIsAmenitiesExpanded(e) {
    this.isAmenitiesExpanded = !this.isAmenitiesExpanded;
    e.stopPropagation();
  }

  loadInvitesTask = task({}, async () => {
    try {
      const startTime = this.selectedStartDateForRequests;
      const endTime = this.selectedEndDateForRequests;
      const filter = {
        locationId: get(this.state.currentLocation, 'id'),
        dateTo: encodeURIComponent(formatISO(endTime)),
        dateFrom: encodeURIComponent(formatISO(startTime)),
        email: encodeURIComponent(this.state.currentUser.email),
      };
      const url = urlBuilder.v3.invites.fetchEmployeeInviteOnDate(filter);
      const response = await this.ajax.request(url, {
        type: 'GET',
        contentType: 'application/vnd.api+json',
        headers: { accept: 'application/vnd.api+json' },
      });

      const activeInvite = response.data.find((invite) => !invite.attributes['entry-signed-out-at']);
      if (activeInvite) {
        let invite = this.store.findRecord('invite', activeInvite.id);
        if (!invite) {
          const normalizedInvite = normalizeResponse(this.store, 'invite', { data: activeInvite });
          invite = this.store.createRecord('invite', normalizedInvite.data);
          setProperties(invite, {
            ...normalizedInvite.data.attributes,
          });
        }
        return invite;
      } else {
        return null;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('error:', e);
    }
  });

  createInviteTask = task({}, async () => {
    const startTime = this.selectedStartDateForRequests;
    const endTime = this.selectedEndDateForRequests;
    const invite = await this.state.currentLocation.createInvite({
      expectedArrivalTime: startTime,
      fullName: this.state.currentUser.fullName,
      email: this.state.currentUser.email,
    });
    invite.endTime = endTime;
    const inviteUserDatum = invite.userData[0] || {};
    // The create invite functions in the location model assign the flow name, userdata defining the flow, and flow rel for the first valid flow it locates.
    // We want to remove the rel relating to a specific flow by id and let the BE determine the proper flow based off the flow-name and userdata.
    invite.flow = null;
    invite.employeeScreeningFlow = true;
    invite.flowName = 'Employee registration';
    inviteUserDatum.value = 'Employee registration';

    await invite.save();
    return invite;
  });

  loadOrCreateInviteTask = task({}, async () => {
    let invite = await this.loadInvitesTask.perform();
    if (!invite) {
      invite = await this.createInviteTask.perform();
    }
    return invite;
  });

  bookDeskTask = task({ drop: true }, async () => {
    const { currentUserReservation, desk, date, loadReservations, loadAvailableDesks, setEmployees } = this.args;
    try {
      let newReservation;
      if (currentUserReservation) {
        const invite = await currentUserReservation.invite;
        const startUnix = this.args.selectedTime != null ? this.args.selectedTime : currentUserReservation.startTime;
        const endUnix = this.args.selectedEndTime != null ? this.args.selectedEndTime : currentUserReservation.endTime;
        newReservation = await currentUserReservation.editReservation(invite, desk, startUnix, endUnix);
      } else {
        const invite = await this.loadOrCreateInviteTask.perform();
        const locationTime = this.state.getOfficeLocationTime(
          startOfDay(subMinutes(date, this.state.minutesBetweenTimezones(date))),
        );
        const reservation = this.store.createRecord('reservation', {
          invite,
          desk,
          user: this.state.currentUser,
          location: this.state.currentLocation,
          startTime: this.args.selectedTime != null ? this.args.selectedTime : getUnixTime(locationTime),
          endTime: this.args.selectedEndTime != null ? this.args.selectedEndTime : getUnixTime(this.locationEndTime),
        });
        newReservation = await reservation.save();
      }

      this.metrics.trackEvent('Web Live Map - Book Desk', {
        current_desk_id: currentUserReservation ? get(currentUserReservation.desk, 'id') : null,
        current_reservation_id: currentUserReservation?.id,
        new_desk_id: currentUserReservation
          ? newReservation.data.relationships.desk.data.id
          : newReservation.belongsTo('desk').id(),
      });

      const employee = await this.fetchEmployeeTask.perform();
      setEmployees(employee);
      loadReservations();
      loadAvailableDesks();
      this.liveMap.restartPollingLiveMap();

      this.flashMessages.showAndHideFlash('success', 'Desk booked');
    } catch (e) {
      console.error('error', e); // eslint-disable-line no-console
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  fetchEmployeeTask = task({}, async () => {
    const employees = await this.store.query('employee', {
      filter: {
        locations: this.state.currentLocation.id,
        'email-in': this.state.currentUser.email,
        deleted: false,
      },
      include: 'user',
      // page: { limit, offset },
    });
    return employees.slice()[0];
  });
}
